/**
 * useCompanyDetail Hook
 *
 * Manages company detail data fetching and state.
 * Preloads related data (insights, knowledge, communication) in background.
 */

import { useState, useEffect, useCallback } from 'react';
import { companiesApi } from '@/services/contactsApi';
import type {
  Company,
  CompanyEmailInsights,
  KnowledgeCard,
  CommunicationResponse,
} from '../types';

interface SuppressedItem {
  item_type: string;
  item_value: string;
}

interface UseCompanyDetailResult {
  company: Company | null;
  loading: boolean;
  error: string | null;
  insights: CompanyEmailInsights | null;
  insightsLoading: boolean;
  knowledge: KnowledgeCard[];
  knowledgeLoading: boolean;
  communication: CommunicationResponse | null;
  communicationLoading: boolean;
  suppressedItems: SuppressedItem[];
  refresh: () => Promise<void>;
  refreshInsights: () => Promise<void>;
  refreshKnowledge: () => Promise<void>;
  refreshCommunication: () => Promise<void>;
  setCompany: React.Dispatch<React.SetStateAction<Company | null>>;
  setKnowledge: React.Dispatch<React.SetStateAction<KnowledgeCard[]>>;
}

export function useCompanyDetail(companyId: string | undefined): UseCompanyDetailResult {
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [insights, setInsights] = useState<CompanyEmailInsights | null>(null);
  const [insightsLoading, setInsightsLoading] = useState(false);

  const [knowledge, setKnowledge] = useState<KnowledgeCard[]>([]);
  const [knowledgeLoading, setKnowledgeLoading] = useState(false);

  const [communication, setCommunication] = useState<CommunicationResponse | null>(null);
  const [communicationLoading, setCommunicationLoading] = useState(false);


  // Load company
  const refresh = useCallback(async () => {
    if (!companyId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await companiesApi.get(companyId);
      setCompany(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load company');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  // Load email insights
  const refreshInsights = useCallback(async () => {
    if (!companyId) return;

    setInsightsLoading(true);
    try {
      const data = await companiesApi.getEmailInsights(companyId);
      setInsights(data);
    } catch (err) {
      console.error('Failed to load company insights:', err);
      setInsights(null);
    } finally {
      setInsightsLoading(false);
    }
  }, [companyId]);

  // Load knowledge cards
  const refreshKnowledge = useCallback(async () => {
    if (!companyId) return;

    setKnowledgeLoading(true);
    try {
      const data = await companiesApi.getKnowledgeCards(companyId);
      setKnowledge(data);
    } catch (err) {
      console.error('Failed to load knowledge cards:', err);
      setKnowledge([]);
    } finally {
      setKnowledgeLoading(false);
    }
  }, [companyId]);

  // Load communication (email threads)
  const refreshCommunication = useCallback(async () => {
    if (!companyId) return;

    setCommunicationLoading(true);
    try {
      const data = await companiesApi.getCommunication(companyId);
      setCommunication(data);
    } catch (err) {
      console.error('Failed to load communication:', err);
      setCommunication(null);
    } finally {
      setCommunicationLoading(false);
    }
  }, [companyId]);

  // Load company on mount/companyId change
  useEffect(() => {
    if (!companyId) {
      setCompany(null);
      setLoading(false);
      return;
    }

    // Reset state
    setInsights(null);
    setKnowledge([]);
    setCommunication(null);

    // Load company
    refresh();

    // Background preload other data (non-blocking)
    companiesApi.getEmailInsights(companyId)
      .then(setInsights)
      .catch(() => setInsights(null));

    companiesApi.getKnowledgeCards(companyId)
      .then(setKnowledge)
      .catch(() => setKnowledge([]));

    companiesApi.getCommunication(companyId)
      .then(setCommunication)
      .catch(() => setCommunication(null));
  }, [companyId, refresh]);

  // Derive suppressed items from knowledge cards
  const suppressedItems = knowledge
    .filter(c => c.card_type === 'suppression')
    .map(card => {
      try {
        const data = JSON.parse(card.content);
        return {
          item_type: data.suppressed_type || '',
          item_value: data.suppressed_content || '',
        };
      } catch {
        return { item_type: '', item_value: '' };
      }
    })
    .filter(item => item.item_type && item.item_value);

  return {
    company,
    loading,
    error,
    insights,
    insightsLoading,
    knowledge,
    knowledgeLoading,
    communication,
    communicationLoading,
    suppressedItems,
    refresh,
    refreshInsights,
    refreshKnowledge,
    refreshCommunication,
    setCompany,
    setKnowledge,
  };
}
